import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "components/Layout";
import Calendar, { toISODate } from "components/Calendar";
import api from "utils/api";
import "styles/booking.css";

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
const HOFFMANN_OPTION1_CUTOFF = new Date(2026, 5, 30, 23, 59, 59, 999);
const HOTEL_SERVICE_KEYS = new Set([
  "dr-hoffmann-hotel-15",
  "dr-hoffmann-hotel-60",
]);
const isAfterHoffmannOption1Cutoff = (date) =>
  Boolean(date) && new Date(date).getTime() > HOFFMANN_OPTION1_CUTOFF.getTime();

const ReschedulePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/bookings/${id}`)
      .then(({ data }) => {
        setBooking(data.booking);
        setSelectedDate(new Date(data.booking.start));
      })
      .catch((err) => setError(err?.response?.data?.message || "Buchung nicht gefunden."));
  }, [id]);

  useEffect(() => {
    if (!selectedDate || !booking) return;
    let cancelled = false;
    setLoadingSlots(true);
    api
      .get("/bookings/availability", {
        params: {
          date: toISODate(selectedDate),
          durationMinutes: booking.durationMinutes,
          brand: "dr-hoffmann",
          serviceKey: booking.serviceKey,
          ...(booking.studioSchedule === "weekday"
            ? { studioSchedule: "weekday" }
            : booking.serviceKey === "dr-hoffmann-studio"
              ? { studioSchedule: "thursday" }
              : {}),
        },
      })
      .then(({ data }) => {
        if (!cancelled) setSlots(data?.slots || []);
      })
      .catch(() => !cancelled && setSlots([]))
      .finally(() => !cancelled && setLoadingSlots(false));
    return () => {
      cancelled = true;
    };
  }, [selectedDate, booking]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.patch(`/bookings/${id}`, { startISO: selectedSlot });
      navigate("/konto/termine", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || "Termin konnte nicht geändert werden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="booking-page">
        <div className="option-detail" style={{ marginTop: 0 }}>
          <div className="booking-step" style={{ borderTop: "none", paddingTop: 0, marginTop: 0 }}>
            <div className="step-head">
              <h2>
                Termin <em>ändern</em>
              </h2>
              <Link to="/konto/termine" style={{ fontSize: "0.78rem", color: "var(--grey-mid)", fontWeight: 600 }}>
                ← Zurück
              </Link>
            </div>
            {error && <div className="auth-alert is-error">{error}</div>}
            {!booking ? (
              <p className="step-summary">Lädt...</p>
            ) : (
              <>
                <p className="step-summary">
                  <strong>{booking.serviceLabel}</strong> · {booking.durationMinutes} Min.
                  · Aktuell:{" "}
                  {new Date(booking.start).toLocaleString("de-DE", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>

                <p className="step-summary">
                  {booking.serviceKey === "dr-hoffmann-studio"
                    ? "Termine sind zwischen 10:00 und 22:00 Uhr buchbar."
                    : HOTEL_SERVICE_KEYS.has(booking.serviceKey)
                      ? "Täglich 10:00–22:00 Uhr · frühestens 1 Stunde im Voraus."
                    : booking.serviceKey === "dr-hoffmann-mobile"
                      ? "Montag bis Freitag, 12:00–18:00 Uhr · 30 Min. Pause nach jedem Termin."
                      : "Montag bis Freitag, 12:00–18:00 Uhr · frühestens 1 Stunde im Voraus."}
                </p>
                <Calendar
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)}
                  weekdaysOnly={
                    !HOTEL_SERVICE_KEYS.has(booking.serviceKey) &&
                    booking.serviceKey !== "dr-hoffmann-studio"
                  }
                  onlyWeekday={booking.serviceKey === "dr-hoffmann-studio" ? 4 : -1}
                  closedWeekday={-1}
                  closedDayTitle={
                    booking.serviceKey === "dr-hoffmann-studio"
                      ? "Studio-Massagen nur Donnerstags"
                      : undefined
                  }
                  disableDate={
                    booking.serviceKey === "dr-hoffmann-studio"
                      ? isAfterHoffmannOption1Cutoff
                      : undefined
                  }
                    disableDateTitle="Studio Massagen sind nur bis Ende Juni buchbar"
                />

                <div style={{ marginTop: "1.5rem" }}>
                  {loadingSlots ? (
                    <p className="step-summary">Verfügbare Zeiten werden geladen...</p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary">Keine freien Zeiten an diesem Tag.</p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="booking-actions">
                  <Link to="/konto/termine" className="btn-ghost">Abbrechen</Link>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSubmit}
                    disabled={!selectedSlot || submitting}
                  >
                    {submitting ? "Speichern…" : "Neuen Termin bestätigen"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReschedulePage;
